import { z } from 'zod'
import type { ChequeSummary } from './cheques.js'
import { sortSchema } from './common.js'
import type { TreasuryKind } from './finance.js'
import type { SupplierRef } from './purchasing.js'
import { reportPeriodIssues } from './reports.js'
import type { CustomerRef } from './sales.js'

/**
 * Operational reports and the home dashboard (S8). Read-only queries over the posted documents and
 * the stock ledger; dates are business dates (YYYY-MM-DD, Asia/Muscat), inclusive at both ends.
 * Sales and purchase figures are ex-VAT unless a field says otherwise; credit notes and supplier
 * returns count on their own dates, so a period nets exactly what was issued in it.
 */

const period = { from: z.iso.date(), to: z.iso.date() }
const paging = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
}
const checkPeriod = (q: { from: string; to: string }, ctx: z.RefinementCtx) => {
  for (const issue of reportPeriodIssues(q.from, q.to)) ctx.addIssue({ code: 'custom', ...issue })
}

// ── shared shapes ────────────────────────────────────────────────────────────

/** A product variant as reports print it ("Product — Variant"). */
export interface ReportVariant {
  variantId: string
  productId: string
  label: string
  labelAr: string | null
  sku: string | null
  unitSymbol: string
}

/** A page of report rows with the totals of every row (not just this page). */
export interface ReportPage<Row, Totals> {
  items: Row[]
  page: number
  pageSize: number
  total: number
  totals: Totals
}

// ── dashboard ────────────────────────────────────────────────────────────────

export const dashboardQuerySchema = z.object({
  /** Default: today (Asia/Muscat). */
  asOf: z.iso.date().optional(),
})
export type DashboardQuery = z.input<typeof dashboardQuerySchema>

export interface DashboardSalesFigure {
  /** Posted invoices and debit notes less posted credit notes, ex-VAT. */
  netSales: string
  /** Posted invoices (debit notes and credit notes not counted). */
  invoiceCount: number
}

export interface DashboardSales {
  today: DashboardSalesFigure
  monthToDate: DashboardSalesFigure
  /** From the start of the fiscal year containing `asOf` (1 January when none covers it). */
  yearToDate: DashboardSalesFigure
  yearStart: string
  /** The 12 calendar months ending with the month of `asOf` (that month to `asOf`), oldest first. */
  months: (DashboardSalesFigure & { month: string })[]
}

export interface DashboardReceivables {
  /** Open invoice balances less unallocated customer credit (the aging total). */
  total: string
  /** Balances past their due date (unallocated credit not deducted). */
  overdue: string
  /** The five customers with the largest overdue balances. */
  topOverdue: { customer: CustomerRef; overdue: string; total: string }[]
}

export interface DashboardPayables {
  /** Open purchase balances less unallocated payments and returns (the aging total). */
  total: string
  /** Balances past their due date. */
  overdue: string
  /** Balances falling due from `asOf` to `asOf` + 7 days, not yet overdue. */
  dueNext7Days: string
}

export interface DashboardCash {
  /** Ledger balance at the end of `asOf` of each cash and bank account (archived ones only if not zero). */
  accounts: { id: string; name: string; nameAr: string | null; kind: TreasuryKind; balance: string }[]
  total: string
}

export interface DashboardStock {
  /** Value at cost of all stock at the end of `asOf` (Σ movement values). */
  value: string
  /** Stocked variants whose total quantity is at or below their reorder level. */
  lowStockCount: number
  /** Up to five of them, the largest shortfall first. */
  lowStock: LowStockRow[]
}

export interface DashboardPos {
  /** Shifts open now. */
  openShifts: number
  /**
   * Till sales on `asOf`, before VAT like the net sales tile: `gross` the completed sales, `returns` the
   * posted credit notes against till sales (till and back-office), `net` = gross − returns.
   */
  today: { salesCount: number; gross: string; returns: string; net: string }
}

/**
 * Projects (projects/06 §5.12), over the projects the user reaches under projects.read; the amounts
 * only with projects.financials (null otherwise), over that permission's scope.
 */
export interface DashboardProjects {
  /** PLANNING, ACTIVE and ON_HOLD projects. */
  active: number
  /** Open projects with an overdue milestone or past their end date (the portfolio's attention view). */
  attention: number
  /** READY billing plan items (ex-VAT). */
  readyToBill: string | null
  /** Open balances of project invoices. */
  outstanding: string | null
  /** P5: retention held on contracts (money) and guarantees expiring within 30 days. Optional for older fixtures. */
  retentionHeld?: string | null
  guaranteesExpiring?: number
}

/**
 * The home screen. Each section is present only when the user holds its permission: sales and
 * receivables need sales.read, payables purchasing.read, cash treasury.read, stock inventory.read,
 * the till section pos.view_shift, and projects projects.read (with the module on).
 */
export interface Dashboard {
  asOf: string
  sales?: DashboardSales
  receivables?: DashboardReceivables
  payables?: DashboardPayables
  cash?: DashboardCash
  stock?: DashboardStock
  pos?: DashboardPos
  projects?: DashboardProjects
  /** AA1: the latest Books health counts for the current month (ledger.read or reports.financial). */
  books?: DashboardBooks
  /**
   * ADR-046: the cheque follow-up's figures as of today (a live follow-up, whatever `asOf`), a side each for those
   * who manage it (received: receivedCheques.manage with sales.read; issued: issuedCheques.manage with
   * purchasing.read).
   */
  cheques?: DashboardCheques
}

export interface DashboardCheques {
  received?: ChequeSummary
  issued?: ChequeSummary
}

/** The latest Books health run for the month (ACCOUNTING_ASSISTANT.md §3.1); counts exclude dismissed findings. */
export interface DashboardBooks {
  from: string
  to: string
  critical: number
  warning: number
  info: number
  /** When the counts were computed (the weekly job or the last time someone opened Books health); null: never. */
  computedAt: string | null
}

// ── sales ────────────────────────────────────────────────────────────────────

/**
 * Sort keys: netSales (default: -netSales), name, code, invoiceCount. `customerId` narrows the report
 * (rows and totals) to one customer: the profile's "net sales this fiscal year" reads it.
 */
export const salesByCustomerQuerySchema = z
  .object({
    ...period,
    ...paging,
    q: z.string().trim().max(200).optional(),
    sort: sortSchema,
    customerId: z.uuid().optional(),
  })
  .superRefine(checkPeriod)
export type SalesByCustomerQuery = z.input<typeof salesByCustomerQuerySchema>

export interface SalesTotals {
  /** Posted invoices (a debit note adds to `invoiced` but is not counted). */
  invoiceCount: number
  creditNoteCount: number
  /** Posted invoices and debit notes, ex-VAT (after discounts). */
  invoiced: string
  /** Posted credit notes (returns and allowances), ex-VAT, as a positive amount. */
  credited: string
  /** invoiced − credited. */
  netSales: string
  /** Output VAT on the same documents, net. */
  tax: string
  /** netSales + tax. */
  total: string
  /**
   * Cost of the goods on the same documents (BR-RPT-002, as sales by product): the stock-ledger value
   * issued on invoices less the cost returned on RETURN credit notes; 0 for debit notes and allowances.
   */
  cost: string
  /** netSales − cost. */
  grossProfit: string
  /** grossProfit ÷ netSales × 100, 2 dp; null when net sales are 0. */
  marginPercent: string | null
}

export interface SalesByCustomerRow extends SalesTotals {
  customer: CustomerRef
}

export type SalesByCustomer = ReportPage<SalesByCustomerRow, SalesTotals> & { from: string; to: string }

/** Sort keys: netSales (default: -netSales), grossProfit, quantity, name. */
export const salesByProductQuerySchema = z
  .object({ ...period, ...paging, q: z.string().trim().max(200).optional(), sort: sortSchema })
  .superRefine(checkPeriod)
export type SalesByProductQuery = z.input<typeof salesByProductQuerySchema>

export interface ProductSalesTotals {
  netSales: string
  /** Cost of goods issued on the invoices less the cost of goods returned on credit notes. */
  cost: string
  grossProfit: string
  /** grossProfit ÷ netSales × 100, 2 dp; null unless net sales are above zero. */
  marginPercent: string | null
}

export interface SalesByProductRow extends ProductSalesTotals {
  /** Null: free-text lines and allowances not tied to a product. */
  variant: ReportVariant | null
  quantitySold: string
  /** Quantity brought back on RETURN credit notes. */
  quantityReturned: string
  netQuantity: string
}

export type SalesByProduct = ReportPage<SalesByProductRow, ProductSalesTotals> & { from: string; to: string }

export const salesByPeriodQuerySchema = z
  .object({ ...period, groupBy: z.enum(['day', 'month']).default('month') })
  .superRefine(checkPeriod)
export type SalesByPeriodQuery = z.input<typeof salesByPeriodQuerySchema>

export interface SalesByPeriodRow extends SalesTotals {
  /** YYYY-MM-DD (day) or YYYY-MM (month). Every period of the range is present, empty ones too. */
  period: string
}

export interface SalesByPeriod {
  from: string
  to: string
  groupBy: 'day' | 'month'
  rows: SalesByPeriodRow[]
  totals: SalesTotals
}

/** Sales and collections per user over a period (legacy user-performance tab, gap G5). */
export const salesByUserQuerySchema = z.object({ ...period }).superRefine(checkPeriod)
export type SalesByUserQuery = z.input<typeof salesByUserQuerySchema>

export interface SalesByUserTotals {
  /** Posted invoices the user created (no posted-by is recorded: created-by is the credit). */
  invoiceCount: number
  /** Posted invoices and debit notes they created, ex-VAT (after discounts). */
  invoiced: string
  /**
   * Posted credit notes against invoices they created (the seller), or unlinked notes they created,
   * ex-VAT, as a positive amount.
   */
  creditNoteCount: number
  credited: string
  /** invoiced − credited. */
  netSales: string
  /**
   * Of `netSales`: completed till sales on the user's own shifts (the cashier), ex-VAT, less the
   * credit notes against them and the unlinked till returns taken on those shifts (by note date).
   * Those notes are in `credited` too: this is a part of netSales, not an addition to it.
   * `count` is the number of till sales.
   */
  tillSales: { count: number; netSales: string }
  /** Posted receipts they recorded (till tenders included), by receipt date. */
  receipts: { count: number; amount: string }
  /** Posted customer refunds they recorded (till refunds included), by refund date. */
  refunds: { count: number; amount: string }
}

export interface SalesByUserRow extends SalesByUserTotals {
  /** Null: documents with no recorded creator (imported or system-made). */
  user: { id: string; name: string } | null
}

/**
 * Per user: sales credited to whoever created the invoice (invoice date), a credit note (note
 * date) to its invoice's creator or, unlinked, to its own creator, collections and refunds to
 * whoever recorded them. Every user with any figure has a row, largest net sales first. VOID and DRAFT documents never count.
 */
export interface SalesByUser {
  from: string
  to: string
  rows: SalesByUserRow[]
  totals: SalesByUserTotals
}

// ── purchasing ───────────────────────────────────────────────────────────────

/** Sort keys: netPurchases (default: -netPurchases), name, code, purchaseCount. */
export const purchasesBySupplierQuerySchema = z
  .object({ ...period, ...paging, q: z.string().trim().max(200).optional(), sort: sortSchema })
  .superRefine(checkPeriod)
export type PurchasesBySupplierQuery = z.input<typeof purchasesBySupplierQuerySchema>

export interface PurchaseTotals {
  purchaseCount: number
  returnCount: number
  /** Posted purchases, ex-VAT (after discounts). */
  purchased: string
  /** Posted supplier returns and allowances, ex-VAT, as a positive amount. */
  returned: string
  /** purchased − returned. */
  netPurchases: string
  /** Input VAT on the same documents, net. */
  tax: string
  /** netPurchases + tax. */
  total: string
}

export interface PurchasesBySupplierRow extends PurchaseTotals {
  supplier: SupplierRef
}

export type PurchasesBySupplier = ReportPage<PurchasesBySupplierRow, PurchaseTotals> & {
  from: string
  to: string
}

/** Sort keys: netPurchases (default: -netPurchases), quantity, name. */
export const purchasesByProductQuerySchema = z
  .object({ ...period, ...paging, q: z.string().trim().max(200).optional(), sort: sortSchema })
  .superRefine(checkPeriod)
export type PurchasesByProductQuery = z.input<typeof purchasesByProductQuerySchema>

export interface ProductPurchaseTotals {
  purchased: string
  returned: string
  netPurchases: string
}

export interface PurchasesByProductRow extends ProductPurchaseTotals {
  /** Null: free-text lines (services, freight) and allowances not tied to a product. */
  variant: ReportVariant | null
  quantityPurchased: string
  /** Quantity sent back on RETURN supplier returns. */
  quantityReturned: string
  netQuantity: string
}

export type PurchasesByProduct = ReportPage<PurchasesByProductRow, ProductPurchaseTotals> & {
  from: string
  to: string
}

// ── inventory ────────────────────────────────────────────────────────────────

export const stockValuationQuerySchema = z.object({
  /** Default: today (Asia/Muscat). */
  asOf: z.iso.date().optional(),
  /** Only this warehouse's movements (value moves with the goods on transfers). */
  warehouseId: z.uuid().optional(),
  q: z.string().trim().max(200).optional(),
  ...paging,
})
export type StockValuationQuery = z.input<typeof stockValuationQuerySchema>

export interface StockValuationRow {
  variant: ReportVariant
  quantity: string
  value: string
  /** value ÷ quantity (6 dp); null unless the quantity is above zero. */
  averageCost: string | null
}

/**
 * Quantity and value at cost per variant at the end of `asOf`, summed from the stock movements.
 * Variants with neither quantity nor value are left out. Over all warehouses the total equals the
 * inventory account's ledger balance on the same date.
 */
export type StockValuation = ReportPage<StockValuationRow, { value: string }> & {
  asOf: string
  warehouseId: string | null
}

export const lowStockQuerySchema = z.object({
  /** total: on hand in all warehouses; warehouse: one row per warehouse and variant. */
  groupBy: z.enum(['total', 'warehouse']).default('total'),
  /** Only this warehouse (implies groupBy=warehouse). */
  warehouseId: z.uuid().optional(),
  q: z.string().trim().max(200).optional(),
  ...paging,
})
export type LowStockQuery = z.input<typeof lowStockQuerySchema>

export interface LowStockRow {
  variant: ReportVariant
  /** Null when grouped over all warehouses. */
  warehouse: { id: string; code: string; name: string } | null
  quantity: string
  reorderLevel: string
  /** reorderLevel − quantity (≥ 0). */
  shortfall: string
}

/**
 * Stocked goods (active products and variants) whose on-hand quantity is at or below the variant's
 * reorder level, the same rule as the stock list's "below reorder" filter. Largest shortfall first.
 */
export interface LowStock {
  groupBy: 'total' | 'warehouse'
  items: LowStockRow[]
  page: number
  pageSize: number
  total: number
}

// ── point of sale ────────────────────────────────────────────────────────────

export const posSummaryQuerySchema = z
  .object({ ...period, terminalId: z.uuid().optional(), userId: z.uuid().optional(), ...paging })
  .superRefine(checkPeriod)
export type PosSummaryQuery = z.input<typeof posSummaryQuerySchema>

export interface PosSummaryTotals {
  /** Completed till sales. */
  salesCount: number
  /** Till sales voided since. */
  voidedCount: number
  /** Completed sales, VAT-inclusive. */
  gross: string
  /** VAT in `gross`. */
  tax: string
  /**
   * Posted credit notes against till sales: till returns, and back-office notes (RETURN or ALLOWANCE)
   * against a till sale's invoice, on that sale's till, cashier and day of the note.
   */
  returnsCount: number
  /** Those credit notes, VAT-inclusive, as a positive amount. */
  returns: string
  /** VAT in `returns`. */
  returnsTax: string
  /** gross − returns (VAT-inclusive). */
  net: string
  /** (gross − tax) − (returns − returnsTax): net till sales before VAT, the sales reports' basis. */
  netExVat: string
  /**
   * Line and sale discounts on the completed sales' invoices, less the discounts carried back by the
   * credit notes in `returns`; ex VAT.
   */
  discounts: string
  /** Posted receipts paying the completed sales, by method. */
  tenders: { cash: string; card: string; bankTransfer: string; other: string }
  /**
   * The part of the completed sales not paid at the till, charged to the customer's account (G23), by
   * invoice date. Always sent by the API; optional for older fixtures.
   */
  onAccount?: string
  /**
   * Posted refunds of till returns, by method (store credit is not paid out). A back-office credit note
   * is never a drawer refund.
   */
  refunds: { cash: string; card: string }
  /** Counted − expected cash of the shifts closed that day (surplus positive, shortage negative). */
  variance: string
}

export interface PosSummaryRow extends PosSummaryTotals {
  /** Sales by invoice date, returns by credit-note date, variances by the day the shift closed. */
  date: string
  terminal: { id: string; code: string; name: string }
  cashier: { id: string; name: string }
}

/** Till takings per day, till and cashier (the shift's cashier). */
export type PosSummary = ReportPage<PosSummaryRow, PosSummaryTotals> & { from: string; to: string }
